import type {
  FollowUpFilter,
  QuickFilterValue,
} from "@/features/common/types/quick-filter";
import type {
  ContactFilterQuery,
  ContactFilterRule,
  ConversationFilterFieldName,
} from "@/features/inbox/types/filter";
import type { IsoDateRange } from "@/utils/date";

import {
  FOLLOW_UP_FILTER,
  QUICK_FILTER_PARAM,
} from "@/features/common/types/quick-filter";

/**
 * One place that knows how each drawer facet is spelled as a filter rule, so the
 * URL, the drawer's own state and the query the list is fetched with can't drift.
 * Both directions live here: `applyQuickFilter` writes the rules, `readQuickFilter`
 * reads them back (the inbox re-derives its URL from the query it holds).
 */

export const EMPTY_DATE_RANGE: IsoDateRange = { start: null, end: null };

export const EMPTY_QUICK_FILTER: QuickFilterValue = {
  followUp: FOLLOW_UP_FILTER.ANY,
  assignees: [],
  tags: [],
  treatments: [],
  products: [],
  departments: [],
  channels: [],
  createdAt: EMPTY_DATE_RANGE,
  lastActiveAt: EMPTY_DATE_RANGE,
};

const FOLLOW_UP_RULE = {
  [FOLLOW_UP_FILTER.DUE]: { field: "followUpDue", value: true },
  [FOLLOW_UP_FILTER.SCHEDULED]: { field: "followUpRequired", value: true },
  [FOLLOW_UP_FILTER.NONE]: { field: "followUpRequired", value: false },
} as const satisfies Record<
  Exclude<FollowUpFilter, "any">,
  { field: ConversationFilterFieldName; value: boolean }
>;

/**
 * The rule fields each facet writes. A facet only ever replaces its OWN fields,
 * and only when it is actually set: applying a channel facet must not delete the
 * `tags` rule a segment was saved with.
 */
const FACET_FIELDS = {
  followUp: ["followUpDue", "followUpRequired"],
  assignees: ["assignee"],
  tags: ["tags"],
  treatments: ["treatment"],
  products: ["product"],
  departments: ["department"],
  channels: ["channel"],
  createdAt: ["createdAt"],
  lastActiveAt: ["lastInteractedAt"],
} as const satisfies Record<
  keyof QuickFilterValue,
  readonly ConversationFilterFieldName[]
>;

/**
 * Stable per-field rule ids. The conversation list's cache key hashes the whole
 * filter, rule ids included, so minting a UUID per parse would give the same URL
 * a new key every time - refetching the list from page one and dropping any bulk
 * selection.
 */
const facetRuleId = (field: ConversationFilterFieldName): string =>
  `facet-${field}`;

const isFacetSet = (
  value: QuickFilterValue,
  key: keyof QuickFilterValue,
): boolean => {
  if (key === "followUp") return value.followUp !== FOLLOW_UP_FILTER.ANY;
  if (key === "createdAt" || key === "lastActiveAt") {
    return Boolean(value[key].start || value[key].end);
  }

  return value[key].length > 0;
};

// Hoisted: readFollowUp runs on every href the sidebar builds.
const FOLLOW_UP_ENTRIES = Object.entries(FOLLOW_UP_RULE) as Array<
  [FollowUpFilter, (typeof FOLLOW_UP_RULE)[keyof typeof FOLLOW_UP_RULE]]
>;

const FOLLOW_UP_VALUES = new Set<string>(Object.values(FOLLOW_UP_FILTER));

const splitCsv = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const setOrDelete = (
  params: URLSearchParams,
  key: string,
  value: string,
): void => {
  if (value) params.set(key, value);
  else params.delete(key);
};

/**
 * A rule-group entry: a rule, a nested group, or the combinator string between
 * them. react-querybuilder types `rules` as a tuple-ish interleaved array that
 * can't be built incrementally, so the editing below works on this plain shape
 * and casts once at the boundary - the same trick `rowsToRuleGroup` uses.
 */
type RuleItem = ContactFilterRule | ContactFilterQuery | string;

const itemsOf = (filter: ContactFilterQuery | null | undefined): RuleItem[] =>
  (filter?.rules ?? []) as unknown as RuleItem[];

const toQuery = (items: RuleItem[]): ContactFilterQuery =>
  ({ rules: items }) as unknown as ContactFilterQuery;

const facetRule = (
  field: ConversationFilterFieldName,
  operator: string,
  value: unknown,
): ContactFilterRule => ({ id: facetRuleId(field), field, operator, value });

/**
 * An open range is expressed with the operator that fits: both edges is
 * `between`, one edge is `after` / `before`. A half-filled `between` would be an
 * incomplete rule the backend can't evaluate.
 */
const dateRule = (
  field: ConversationFilterFieldName,
  range: IsoDateRange,
): ContactFilterRule | null => {
  if (range.start && range.end) {
    return facetRule(field, "between", {
      start: range.start,
      end: range.end,
    });
  }
  if (range.start) return facetRule(field, "after", range.start);
  if (range.end) return facetRule(field, "before", range.end);

  return null;
};

/**
 * Drop the named fields from an interleaved [rule, combinator, rule, …] list,
 * carrying the combinator of a removed rule onto its successor so a deleted OR
 * branch doesn't quietly become an AND. Nested groups pass through untouched -
 * flattening them here is what used to delete a segment's parenthesised rules.
 */
const dropRuleFields = (
  items: RuleItem[],
  fields: ReadonlySet<string>,
): RuleItem[] => {
  const kept: RuleItem[] = [];
  let pending: string | null = null;

  for (const item of items) {
    if (typeof item === "string") {
      pending = pending ?? item;
      continue;
    }

    if (!("rules" in item) && fields.has((item as ContactFilterRule).field)) {
      continue;
    }

    if (kept.length > 0) kept.push(pending ?? "and");
    kept.push(item);
    pending = null;
  }

  return kept;
};

/** Drop every rule on the named fields, keeping the rest of the query intact. */
export const withoutRuleFields = (
  filter: ContactFilterQuery | null | undefined,
  fields: readonly ConversationFilterFieldName[],
): ContactFilterQuery =>
  toQuery(dropRuleFields(itemsOf(filter), new Set<string>(fields)));

/** True when the list ORs anything, so ANDing onto it needs parentheses. */
const hasOr = (items: RuleItem[]): boolean => items.includes("or");

/**
 * The drawer's own rules, found by the stable ids `applyQuickFilter` stamps.
 * Matching on the id rather than the field name is what keeps a custom view's
 * `tags hasNoneOf […]` from being read back as a tag selection and re-emitted as
 * `hasAnyOf` - i.e. inverted.
 */
const facetRulesById = (
  filter: ContactFilterQuery | null | undefined,
): Map<string, ContactFilterRule> => {
  const found = new Map<string, ContactFilterRule>();

  const walk = (items: RuleItem[]): void => {
    for (const item of items) {
      if (typeof item === "string") continue;
      if ("rules" in item) {
        walk(itemsOf(item as ContactFilterQuery));
        continue;
      }

      const rule = item as ContactFilterRule;

      if (rule.id?.startsWith("facet-")) found.set(rule.id, rule);
    }
  };

  walk(itemsOf(filter));

  return found;
};

const readDateRange = (
  facets: Map<string, ContactFilterRule>,
  field: ConversationFilterFieldName,
): IsoDateRange => {
  const rule = facets.get(facetRuleId(field));

  if (!rule) return EMPTY_DATE_RANGE;

  if (rule.operator === "between") {
    const range = rule.value as IsoDateRange | null;

    return { start: range?.start ?? null, end: range?.end ?? null };
  }

  const edge = typeof rule.value === "string" ? rule.value : null;

  if (rule.operator === "after") return { start: edge, end: null };
  if (rule.operator === "before") return { start: null, end: edge };

  return EMPTY_DATE_RANGE;
};

const readIds = (
  facets: Map<string, ContactFilterRule>,
  field: ConversationFilterFieldName,
): string[] => {
  const rule = facets.get(facetRuleId(field));

  if (!rule || rule.operator !== "hasAnyOf") return [];

  return Array.isArray(rule.value) ? rule.value.map(String) : [];
};

const readFollowUp = (
  facets: Map<string, ContactFilterRule>,
): FollowUpFilter => {
  for (const [option, spec] of FOLLOW_UP_ENTRIES) {
    const rule = facets.get(facetRuleId(spec.field));

    if (rule && rule.value === spec.value) return option;
  }

  return FOLLOW_UP_FILTER.ANY;
};

/** The rules one facet value spells out, in a stable order. */
const quickFilterRules = (value: QuickFilterValue): ContactFilterRule[] => {
  const rules: ContactFilterRule[] = [];

  if (value.followUp !== FOLLOW_UP_FILTER.ANY) {
    const rule = FOLLOW_UP_RULE[value.followUp];

    rules.push(facetRule(rule.field, "isEqualTo", rule.value));
  }

  if (value.assignees.length > 0) {
    rules.push(facetRule("assignee", "hasAnyOf", value.assignees));
  }

  if (value.tags.length > 0) {
    rules.push(facetRule("tags", "hasAnyOf", value.tags));
  }

  if (value.treatments.length > 0) {
    rules.push(facetRule("treatment", "hasAnyOf", value.treatments));
  }

  if (value.products.length > 0) {
    rules.push(facetRule("product", "hasAnyOf", value.products));
  }

  if (value.departments.length > 0) {
    rules.push(facetRule("department", "hasAnyOf", value.departments));
  }

  if (value.channels.length > 0) {
    rules.push(facetRule("channel", "hasAnyOf", value.channels));
  }

  const created = dateRule("createdAt", value.createdAt);
  const lastActive = dateRule("lastInteractedAt", value.lastActiveAt);

  if (created) rules.push(created);
  if (lastActive) rules.push(lastActive);

  return rules;
};

/**
 * AND the drawer's rules onto a query, replacing only the fields whose facet is
 * set and leaving every other rule - a segment's own rules, a custom view's, the
 * sidebar's team pick - exactly as it was, nested groups included.
 *
 * Idempotent, so re-applying the same value (as the inbox does on every URL
 * parse) can't stack duplicates. When the base ORs anything it is parenthesised
 * first: appending `and channel=x` to `a or b` would otherwise only narrow `b`,
 * since AND binds tighter.
 */
export const applyQuickFilter = (
  filter: ContactFilterQuery | null | undefined,
  value: QuickFilterValue,
): ContactFilterQuery => {
  const replaced = new Set<string>(
    (Object.keys(FACET_FIELDS) as Array<keyof QuickFilterValue>)
      .filter((key) => isFacetSet(value, key))
      .flatMap((key) => [...FACET_FIELDS[key]]),
  );
  const base = dropRuleFields(itemsOf(filter), replaced);
  const facets = quickFilterRules(value);

  if (facets.length === 0) return toQuery(base);

  const items: RuleItem[] =
    base.length > 1 && hasOr(base) ? [toQuery(base)] : [...base];

  facets.forEach((rule) => {
    if (items.length > 0) items.push("and");
    items.push(rule);
  });

  return toQuery(items);
};

export const readQuickFilter = (
  filter: ContactFilterQuery | null | undefined,
): QuickFilterValue => {
  const facets = facetRulesById(filter);

  return {
    followUp: readFollowUp(facets),
    assignees: readIds(facets, "assignee"),
    tags: readIds(facets, "tags"),
    treatments: readIds(facets, "treatment"),
    products: readIds(facets, "product"),
    departments: readIds(facets, "department"),
    channels: readIds(facets, "channel"),
    createdAt: readDateRange(facets, "createdAt"),
    lastActiveAt: readDateRange(facets, "lastInteractedAt"),
  };
};

export const parseQuickFilterParams = (
  params: URLSearchParams,
): QuickFilterValue => {
  const followUp = params.get(QUICK_FILTER_PARAM.FOLLOW_UP);

  return {
    followUp:
      followUp && FOLLOW_UP_VALUES.has(followUp)
        ? (followUp as FollowUpFilter)
        : FOLLOW_UP_FILTER.ANY,
    assignees: splitCsv(params.get(QUICK_FILTER_PARAM.ASSIGNEES)),
    tags: splitCsv(params.get(QUICK_FILTER_PARAM.TAGS)),
    treatments: splitCsv(params.get(QUICK_FILTER_PARAM.TREATMENTS)),
    products: splitCsv(params.get(QUICK_FILTER_PARAM.PRODUCTS)),
    departments: splitCsv(params.get(QUICK_FILTER_PARAM.DEPARTMENTS)),
    channels: splitCsv(params.get(QUICK_FILTER_PARAM.CHANNELS)),
    createdAt: {
      start: params.get(QUICK_FILTER_PARAM.CREATED_FROM),
      end: params.get(QUICK_FILTER_PARAM.CREATED_TO),
    },
    lastActiveAt: {
      start: params.get(QUICK_FILTER_PARAM.ACTIVE_FROM),
      end: params.get(QUICK_FILTER_PARAM.ACTIVE_TO),
    },
  };
};

export const writeQuickFilterParams = (
  params: URLSearchParams,
  value: QuickFilterValue,
): void => {
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.FOLLOW_UP,
    value.followUp === FOLLOW_UP_FILTER.ANY ? "" : value.followUp,
  );
  setOrDelete(params, QUICK_FILTER_PARAM.ASSIGNEES, value.assignees.join(","));
  setOrDelete(params, QUICK_FILTER_PARAM.TAGS, value.tags.join(","));
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.TREATMENTS,
    value.treatments.join(","),
  );
  setOrDelete(params, QUICK_FILTER_PARAM.PRODUCTS, value.products.join(","));
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.DEPARTMENTS,
    value.departments.join(","),
  );
  setOrDelete(params, QUICK_FILTER_PARAM.CHANNELS, value.channels.join(","));
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.CREATED_FROM,
    value.createdAt.start ?? "",
  );
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.CREATED_TO,
    value.createdAt.end ?? "",
  );
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.ACTIVE_FROM,
    value.lastActiveAt.start ?? "",
  );
  setOrDelete(
    params,
    QUICK_FILTER_PARAM.ACTIVE_TO,
    value.lastActiveAt.end ?? "",
  );
};

/** How many facets are set - the badge on the Filters button. */
export const countQuickFilters = (value: QuickFilterValue): number =>
  (Object.keys(FACET_FIELDS) as Array<keyof QuickFilterValue>).filter((key) =>
    isFacetSet(value, key),
  ).length;
